import React from 'react';

export default useOnClickOutside;

/*
 * This hook allows you to detect clicks outside of a specified element.
 *
 * Example:
 * ```
 * const [isOpen, setIsOpen] = useState(false);
 * const modalRef = useRef<HTMLDivElement>(null);
 *
 * useOnClickOutside(modalRef, () => isOpen && setIsOpen(false));
 *
 * ```
 */
function useOnClickOutside(
  ref: React.MutableRefObject<HTMLElement | null>,
  handler: (event: MouseEvent) => void
): void {
  React.useEffect(() => {
    window.addEventListener('mousedown', listener);

    function listener(event: MouseEvent): void {
      const node = event.target as Node;
      const isClickedOutside =
        ref && ref.current && !ref.current.contains(node);

      if (isClickedOutside && node.textContent !== 'Edit') {
        handler(event);
      }
    }

    return (): void => window.removeEventListener('mousedown', listener);
  }, [ref, handler]);
}
